import React, { useMemo } from 'react';
import css from './BookingItem.module.css';
import { NamedLink } from '@/components';
import moment, { Moment } from 'moment';
import SanitizedHTML from '@/v2/bookings/listBookings/SanitizedHTML.tsx';
import { CpLeadLine } from '@server/v2/odoo/types.ts';
import { BookingListType } from '@/v2/schemas.ts';
import { BookingEventData } from '@server/v2/odoo/schemas.ts';

export interface BookingItemProps {
  listingType: BookingListType;
  booking: BookingEventData;
  link?: boolean;
  isContractCustomer?: boolean;
  /** Extra content shown at the bottom of the card, e.g. actions */
  children?: React.ReactNode;
}

function useMoment(value: string): Moment | null {
  return useMemo(() => (value ? moment(value) : null), [value]);
}

interface FieldDef {
  label: string;
  value: string;
  html?: boolean;
  link?: boolean;
}

export default function BookingItem({
  listingType,
  booking,
  link,
  isContractCustomer,
  children,
}: BookingItemProps) {
  const startDate = useMoment(booking.start!);
  const endDate = useMoment(booking.stop!);

  // Find fee/travelFee
  // The fields are only returned from the backend if I'm the professional for that lead line
  let fee;
  let travelFee;
  if (booking.cpLeadLines) {
    for (const line of booking.cpLeadLines as CpLeadLine[]) {
      if (line.fee) fee = line.fee;
      if (line.travelFee) travelFee = line.travelFee;
    }
  }

  function statusLabel(value) {
    return (
      {
        New: 'New request',
        'Closed Won': 'Confirmed',
        // TODO: there can be other reasons it is Closed Lost...
        'Closed Lost': 'Cancelled',
      }[value] ?? value
    );
  }

  // The address is on the event, but fall back to the lead's copy of it
  const address =
    booking.locationAddress || booking.opportunityId.locationAddress;

  const fields = [
    {
      label: `Booking reference: ${booking.name}`, // not sure how to do booking reference numbers
      value:
        startDate && endDate
          ? `${startDate.format('DD/MM/YYYY HH:mm')} - ${endDate.format('HH:mm')}`
          : '',
      link,
    },
    {
      label: 'Status',
      value: statusLabel((booking.opportunityId.stageId as any)?.displayName),
    },
    {
      label: 'Booking Type',
      value: (booking.opportunityId.bookingTypeId as any)?.displayName,
    },
    {
      label: 'Professional',
      value: (booking.opportunityId.professionalTypeId as any)?.displayName,
    },
    {
      label: 'Location name',
      value:
        booking.faceToFaceSelection === 'remote'
          ? 'Online'
          : booking.locationName,
    },
    booking.faceToFaceSelection !== 'remote' &&
      address && {
        label: 'Address',
        value: address,
      },
    booking.locationDepartment && {
      label: 'Department',
      value: booking.locationDepartment,
    },
    listingType === 'your-work' && {
      label: 'Fee',
      html: true,
      value: fee && `&pound;${fee.toFixed(2)}`,
    },
    listingType === 'your-work' && {
      label: 'Travel fee',
      html: true,
      value: travelFee && `&pound;${travelFee.toFixed(2)}`,
    },
    listingType === 'by-you' &&
      !isContractCustomer && {
        label: 'Amount',
        html: true,
        value:
          booking.opportunityId.expectedRevenue &&
          `&pound;${booking.opportunityId.expectedRevenue.toFixed(2)}`,
      },
    listingType === 'by-you' &&
      !isContractCustomer && {
        label: 'Travel amount',
        html: true,
        value:
          booking.opportunityId.travelAmount &&
          `&pound;${booking.opportunityId.travelAmount.toFixed(2)}`,
      },
    {
      label: 'Description',
      value: booking.description,
      html: true,
    },
    {
      label: 'Professional booked',
      value: booking.cpLeadLines
        ?.map((cpLeadLine) => (cpLeadLine as any).cpId?.displayName)
        .join(', '),
    },
    /*
    {
      label: 'Amount',
      value: 'TBC', // not sure which field to take it from
    },

     */
  ].filter((field) => Boolean(field)) as FieldDef[];

  return (
    <div className={css.booking}>
      {fields.map((field, idx) => (
        <div key={idx}>
          <h3>
            {field.link ? (
              <NamedLink
                name="ViewBookingPage"
                params={{ bookingId: booking.id }}
              >
                {field.label}
              </NamedLink>
            ) : (
              field.label
            )}
          </h3>
          {field.value ? (
            field.html ? (
              <SanitizedHTML rawHTML={field.value} />
            ) : (
              <p>{field.value}</p>
            )
          ) : (
            <p>TBC</p>
          )}
        </div>
      ))}
      {children && <div className={css.actions}>{children}</div>}
    </div>
  );
}
